import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Grid,
  Fade,
  Slide,
  InputAdornment,
  IconButton,
  alpha,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import logo from '../../assets/img/logo.png';
import loginScreen1 from '../../assets/img/loginScreen.jpg';
import loginScreen2 from '../../assets/img/loginScreen2.jpg';
import loginScreen3 from '../../assets/img/loginScreen3.jpg';
import { useHistory } from 'react-router-dom';
import { saveObject, toastError } from '../../utils';
import LoginFormValidate from './LoginFormValidate';
import Loader, { InlineLoader } from '../../components/Loader';
import { adminLogin } from './actions';

const loginBackgrounds = [loginScreen1, loginScreen2, loginScreen3];
const loginScreen = loginBackgrounds[Math.floor(Math.random() * loginBackgrounds.length)];

// Left Side - Image Panel with Dark Overlay
const ImagePanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `url(${loginScreen})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg,
      ${alpha('#0F172A', 0.75)} 0%,
      ${alpha('#1E293B', 0.70)} 50%,
      ${alpha('#0F172A', 0.80)} 100%)`,
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 50%, ${alpha(theme.palette.primary.main, 0.25)} 0%, transparent 50%)`,
    zIndex: 2,
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 0.8 },
  },
}));

const ImageContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 3,
  padding: theme.spacing(4),
  color: '#fff',
  maxWidth: 450,
  textAlign: 'center',
}));

const FloatingParticle = styled(Box)(({ theme, size, delay, duration }) => ({
  position: 'absolute',
  width: size || '4px',
  height: size || '4px',
  backgroundColor: alpha('#12B4A1', 0.6),
  borderRadius: '50%',
  boxShadow: `0 0 ${size || '4px'} ${alpha('#12B4A1', 0.8)}`,
  animation: `float ${duration || '20s'} ease-in-out ${delay || '0s'} infinite`,
  zIndex: 3,
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0) translateX(0)',
      opacity: 0,
    },
    '10%': {
      opacity: 1,
    },
    '90%': {
      opacity: 1,
    },
    '100%': {
      transform: 'translateY(-100vh) translateX(50px)',
      opacity: 0,
    },
  },
}));

// Right Side - Login Form Panel
const FormPanel = styled(Box)(({ theme }) => ({
  height: '100vh',
  background: `linear-gradient(135deg,
    ${alpha('#0F172A', 1)} 0%,
    ${alpha('#1E293B', 0.98)} 50%,
    ${alpha('#0F172A', 1)} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '500px',
    height: '500px',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
    borderRadius: '50%',
    animation: 'breathe 10s ease-in-out infinite',
    zIndex: 1,
  },
  '@keyframes breathe': {
    '0%, 100%': {
      transform: 'scale(1)',
      opacity: 0.5,
    },
    '50%': {
      transform: 'scale(1.2)',
      opacity: 0.8,
    },
  },
}));

const LoginCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  background: alpha('#1E293B', 0.4),
  backdropFilter: 'blur(24px) saturate(180%)',
  borderRadius: 24,
  padding: theme.spacing(5, 4),
  boxShadow: `
    0 8px 32px ${alpha('#000', 0.4)},
    0 0 0 1px ${alpha('#fff', 0.06)} inset,
    0 0 60px ${alpha(theme.palette.primary.main, 0.15)}
  `,
  border: `1px solid ${alpha('#fff', 0.08)}`,
  maxWidth: 480,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha('#0F172A', 0.5),
    color: '#E2E8F0',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: alpha('#fff', 0.12),
      borderWidth: 1,
    },
    '&:hover': {
      backgroundColor: alpha('#0F172A', 0.7),
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.6),
      },
    },
    '&.Mui-focused': {
      backgroundColor: alpha('#0F172A', 0.6),
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
  '& .MuiInputLabel-root': {
    color: alpha('#E2E8F0', 0.7),
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#E2E8F0',
  },
  '& .MuiFormHelperText-root': {
    color: alpha('#FF6B6B', 0.95),
    marginLeft: 0,
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primary.gradient,
  color: '#fff',
  padding: '16px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 12,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.45)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: theme.palette.primary.gradientHover,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.6)}`,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: alpha('#64748B', 0.3),
    color: alpha('#fff', 0.5),
  },
}));

const LogoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    maxWidth: 180,
    height: 'auto',
    filter: 'brightness(1.2) drop-shadow(0 4px 16px rgba(18, 180, 161, 0.4))',
  },
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  color: alpha('#12B4A1', 0.95),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 26,
  '& .MuiChip-icon': {
    color: alpha('#12B4A1', 0.9),
    fontSize: 16,
  },
}));

const BrandFeature = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  backgroundColor: alpha('#12B4A1', 0.08),
  borderRadius: 12,
  border: `1px solid ${alpha('#12B4A1', 0.2)}`,
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  flex: '1 1 0',
  minWidth: 100,
  maxWidth: 120,
  '&:hover': {
    backgroundColor: alpha('#12B4A1', 0.12),
    transform: 'translateY(-3px)',
    boxShadow: `0 6px 18px ${alpha('#12B4A1', 0.2)}`,
  },
}));

// Hidden image element used to detect when background image has loaded
const BackgroundImage = styled('img')({
  position: 'absolute',
  width: 0,
  height: 0,
  opacity: 0,
  pointerEvents: 'none',
});

export default function Login() {
  const history = useHistory();
  const [user, setUser] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [imageLoader, setImageLoader] = useState(true);
  const [loginLoader, setLoginLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Memoize particle data so it doesn't change on every render
  const particles = useMemo(() => {
    return [...Array(15)].map((_, i) => ({
      id: i,
      size: `${Math.random() * 6 + 2}px`,
      delay: `${Math.random() * 5}s`,
      duration: `${Math.random() * 10 + 15}s`,
      left: `${Math.random() * 100}%`,
    }));
  }, []);

  const onChangeHandler = ({ target: { value = '', name = '' } }) => {
    setUser({ ...user, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (loginLoader) return;
    const { errors, isValid } = LoginFormValidate(user);
    setErrors(errors);
    if (isValid) {
      setLoginLoader(true);
      adminLogin(user)
        .then((res) => {
          setLoginLoader(false);
          saveObject('auth', res.token);
          if (res.body?.name) saveObject('labelName', res.body.name);
          return history.push('/');
        })
        .catch((err) => {
          setLoginLoader(false);
          toastError(err.message || 'Something went wrong');
        });
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Fronic Media Labels</title>
      </Helmet>
      <CssBaseline />
      <Grid container sx={{ height: '100vh' }}>
        {/* Left Side - Image Panel with Magic Effects */}
        <Grid
          item
          xs={false}
          md={7}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
          }}
        >
          <ImagePanel>
            <Loader
              show={imageLoader}
              style={{ height: '100%', overflow: 'hidden' }}
            >
              <BackgroundImage
                src={loginScreen}
                alt=""
                onLoad={() => setImageLoader(false)}
              />
            </Loader>

            {/* Floating Particles */}
            {particles.map((particle) => (
              <FloatingParticle
                key={particle.id}
                size={particle.size}
                delay={particle.delay}
                duration={particle.duration}
                sx={{
                  left: particle.left,
                  bottom: '-10px',
                }}
              />
            ))}

            {/* Content Overlay */}
            <ImageContent>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      backgroundColor: alpha('#0F172A', 0.35),
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      px: 3,
                      py: 2,
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                      boxShadow: `0 6px 24px ${alpha('#000', 0.2)}`,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        mb: 0.5,
                        background: 'linear-gradient(135deg, #fff 0%, #12B4A1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em',
                        textShadow: '0 4px 20px rgba(18, 180, 161, 0.3)',
                      }}
                    >
                      Fronic Media
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: alpha('#fff', 0.9),
                        letterSpacing: '0.02em',
                      }}
                    >
                      Label Dashboard
                    </Typography>
                  </Box>

                  {/* Brand Features */}
                  <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
                    <Slide direction="up" in={true} timeout={600}>
                      <BrandFeature>
                        <SecurityIcon sx={{ fontSize: 28, color: '#12B4A1', mb: 0.5 }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}>
                          Enterprise
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontSize: '0.65rem' }}>
                          Security
                        </Typography>
                      </BrandFeature>
                    </Slide>
                    <Slide direction="up" in={true} timeout={800}>
                      <BrandFeature>
                        <SpeedIcon sx={{ fontSize: 28, color: '#12B4A1', mb: 0.5 }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}>
                          Lightning
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontSize: '0.65rem' }}>
                          Fast
                        </Typography>
                      </BrandFeature>
                    </Slide>
                    <Slide direction="up" in={true} timeout={1000}>
                      <BrandFeature>
                        <CloudDoneIcon sx={{ fontSize: 28, color: '#12B4A1', mb: 0.5 }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}>
                          Cloud
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontSize: '0.65rem' }}>
                          Powered
                        </Typography>
                      </BrandFeature>
                    </Slide>
                  </Box>
                </Box>
              </Fade>
            </ImageContent>
          </ImagePanel>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid item xs={12} md={5}>
          <FormPanel>
            <Fade in={true} timeout={800}>
              <LoginCard>
                {/* Security Badge */}
                <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
                  <FeatureChip
                    icon={<VerifiedUserIcon />}
                    label="Secured"
                    size="small"
                  />
                </Box>

                {/* Logo */}
                <LogoBox>
                  <img src={logo} alt="Fronic Logo" />
                </LogoBox>

                {/* Welcome Text */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#F1F5F9',
                      mb: 0.5,
                      background: 'linear-gradient(135deg, #F1F5F9 0%, #12B4A1 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Welcome Back
                  </Typography>
                                  </Box>

                {/* Feature Chips */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <FeatureChip icon={<SecurityIcon />} label="SSL Encrypted" size="small" />
                  <FeatureChip icon={<SpeedIcon />} label="Fast Access" size="small" />
                </Box>

                {/* Login Form */}
                <Box component="form" onSubmit={onSubmit} noValidate>
                  <StyledTextField
                    fullWidth
                    type="email"
                    name="email"
                    label="Email Address"
                    value={user.email}
                    onChange={onChangeHandler}
                    error={!!errors.email}
                    helperText={errors.email}
                    autoComplete="email"
                    autoFocus
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: alpha('#12B4A1', 0.8), fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    label="Password"
                    value={user.password}
                    onChange={onChangeHandler}
                    error={!!errors.password}
                    helperText={errors.password}
                    autoComplete="current-password"
                    sx={{ mb: 3.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: alpha('#12B4A1', 0.8), fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: alpha('#CBD5E1', 0.6),
                              '&:hover': { color: alpha('#12B4A1', 0.9) },
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <GradientButton type="submit" fullWidth disabled={loginLoader}>
                    {loginLoader ? (
                      <InlineLoader size="small" />
                    ) : (
                      'Sign In'
                    )}
                  </GradientButton>
                </Box>

                {/* Footer */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: alpha('#94A3B8', 0.65), fontSize: '0.72rem' }}>
                    © {new Date().getFullYear()} Fronic Media. All rights reserved.
                  </Typography>
                </Box>
              </LoginCard>
            </Fade>
          </FormPanel>
        </Grid>
      </Grid>
    </>
  );
}
